// hooks/useDropdowns.ts
import { useState, useEffect } from 'react';
import { getAuthHeaders } from '../../../../utils/getauthheaders';
import type { Driver } from '@/types/drivers';
import type { Locations } from '@/types/locations';

const API_BASE_URL = `${import.meta.env.VITE_BACKEND_URL}/api`;


type Vehicle = {
  id: number;
  denumire: string;
  marca?: string;
  model?: string;
  numar_inmatriculare?: string;
  capacitate?: number;
};

type Client = {
  id: number;
  denumire: string;
  nume_firma?: string;
  cui?: string;
  telefon?: string;
  location_id?: number;
};


type City = {
  nume: string;
  judet?: string;
};
type DropdownError = {
  message: string;
  code?: number;
};
type DropdownsData = {
  drivers: Driver[];
  vehicles: Vehicle[];
  clients: Client[];
  locations: Locations[];
  cities: City[];
};

type UseDropdownsReturn = {
  data: DropdownsData;
  loading: boolean;
  error: DropdownError | null;
  refetch: () => Promise<void>;
  getLocationsByCity: (city: string) => Locations[];
  getLocationsByType: (type: 'plecare' | 'destinatie') => Locations[];
  getLocationsByCityAndType: (city: string, type: 'plecare' | 'destinatie') => Locations[];
};

// -- CACHE la nivel de modul --
let cachedData: DropdownsData | null = null;
let cacheTimestamp: number | null = null;
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minute cache, poți ajusta

export const useDropdowns = (): UseDropdownsReturn => {
  const [data, setData] = useState<DropdownsData>({
    drivers: [],
    vehicles: [],
    clients: [],
    locations: [],
    cities: []
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<DropdownError | null>(null);
  
  const fetchDropdowns = async () => {
  const now = Date.now();
  if (cachedData && cacheTimestamp && (now - cacheTimestamp < CACHE_DURATION_MS)) {
    setData(cachedData);
    setLoading(false);
    setError(null);
    return;
  }

  try {
    setLoading(true);
    setError(null);

    const response = await fetch(`${API_BASE_URL}/dropdowns/all`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Sesiunea a expirat');
      }
      throw new Error('Eroare la încărcarea datelor');
    }

    const result = await response.json();

    // În useDropdowns, după setData(result.data):
if (result.success) {
  cachedData = result.data;
  cacheTimestamp = Date.now();
  
  // DEBUG: Verifică structura datelor
  console.log('Dropdown data loaded:', result.data);
  console.log('Drivers array:', result.data.drivers);
  console.log('Drivers length:', result.data.drivers?.length || 0);
  
  setData(result.data);
} else {
  throw new Error(result.message || 'Eroare la încărcarea datelor');
}
  } catch (err: unknown) {
    console.error('Eroare fetch dropdowns:', err);

    // Sigur extragerea mesajului
    let message = 'Eroare la încărcarea datelor';
    if (err instanceof Error) {
      message = err.message;
    } else if (typeof err === 'string') {
      message = err;
    }

    setError({ message });
    setData({
      drivers: [],
      vehicles: [],
      clients: [],
      locations: [],
      cities: []
    });
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchDropdowns();
  }, []);

  // Helper functions pentru filtrare
  const getLocationsByCity = (city: string): Locations[] => {
  return data.locations.filter(location => 
    location.oras?.toLowerCase() === city.toLowerCase()
  );
};

  const getLocationsByType = (type: 'plecare' | 'destinatie'): Locations[] => {
    return data.locations.filter(location => location.tip === type);
  };

  const getLocationsByCityAndType = (city: string, type: 'plecare' | 'destinatie'): Locations[] => {
    return data.locations.filter(location => 
      location.oras?.toLowerCase() === city.toLowerCase() && location.tip === type
    );
  };

  return {
    data,
    loading,
    error,
    refetch: fetchDropdowns,
    getLocationsByCity,
    getLocationsByType,
    getLocationsByCityAndType
  };
};
